import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ShoppingBag, Instagram, Youtube, Trash2, User, LogIn } from "lucide-react";
import axios from "axios";
import { useCart } from "./CartContext";
import { useAuth } from "./auth";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MEDIA } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// User-requested strict order:
// Services · Portfolio · Service Areas · Get a Quote · Book a 1:1 Color Consult
// · Paints we use · Free Tools · Our Blog · About
// (Shop items surfaced as a secondary strip below the primary nav.)
const NAV = [
  { to: "/contact", label: "Get a Quote" },
  { to: "/consult", label: "Book a 1:1 Color Consult" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/paints", label: "Paints we use" },
  { to: "/free-tools", label: "Free Tools" },
  { to: "/blog", label: "Our Blog" },
  { to: "/about", label: "About" },
];

const SHOP_LINKS = [
  { to: "/courses", label: "Color Courses" },
  { to: "/renderings", label: "Renderings" },
  { to: "/refer", label: "Refer & Earn" },
];

export function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const { count, setOpen } = useCart();
  const auth = useAuth();
  const user = auth?.user;
  const location = useLocation();
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobile(false); }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${solid ? "bg-ivory/95 backdrop-blur-xl border-b border-black/10 py-3" : "bg-transparent py-5"}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between gap-x-6">
        <Link to="/" data-testid="logo-link" className={`font-serif text-2xl tracking-tight shrink-0 whitespace-nowrap ${solid ? "text-ink" : "text-white"}`}>
          <span className="font-semibold">BH</span> <span className="overline align-middle ml-1">Paint Co.</span>
        </Link>
        <nav className="hidden xl:flex items-center gap-x-4">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) => `overline text-[10px] whitespace-nowrap transition-colors hover:text-gold ${solid ? "text-ink" : "text-white"} ${isActive ? "text-gold" : ""}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <button data-testid="cart-button" onClick={() => setOpen(true)} className={`relative ${solid ? "text-ink" : "text-white"} hover:text-gold transition-colors`}>
            <ShoppingBag size={20} />
            {count > 0 && <span className="absolute -top-2 -right-2 bg-gold text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">{count}</span>}
          </button>
          {user ? (
            <Link to="/portal" data-testid="nav-portal" className={`hidden md:inline-flex items-center gap-2 overline text-[10px] whitespace-nowrap ${solid ? "text-ink" : "text-white"} hover:text-gold`}>
              <User size={14} /> Portal
            </Link>
          ) : (
            <Link to="/login" data-testid="nav-signin" className={`hidden md:inline-flex items-center gap-2 overline text-[10px] whitespace-nowrap ${solid ? "text-ink" : "text-white"} hover:text-gold`}>
              <LogIn size={14} /> Sign In
            </Link>
          )}
          <button className={`xl:hidden flex items-center gap-2 ${solid ? "text-ink" : "text-white"}`} onClick={() => setMobile(!mobile)} data-testid="mobile-menu-toggle">
            <span className="overline">{mobile ? "Close" : "Menu"}</span>
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {/* Secondary Shop strip — surfaced separately per user request */}
      <div className={`hidden xl:block border-t ${solid ? "border-black/5 bg-ivory/80" : "border-white/10 bg-black/20"} transition-colors`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-2 flex items-center gap-x-8">
          <span className={`overline text-[10px] ${solid ? "text-ink/50" : "text-white/60"}`}>Shop</span>
          {SHOP_LINKS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) => `overline text-[10px] whitespace-nowrap transition-colors hover:text-gold ${solid ? "text-ink/80" : "text-white/90"} ${isActive ? "text-gold" : ""}`}
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      </div>
      {mobile && (
        <div className="xl:hidden bg-ivory border-t border-black/10 mt-3 px-6 py-6 flex flex-col gap-4 max-h-[75vh] overflow-y-auto" data-testid="mobile-menu">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setMobile(false)} className="overline text-ink hover:text-gold" data-testid={`mnav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{n.label}</NavLink>
          ))}
          <div className="h-px bg-black/10 my-2" />
          <span className="overline text-ink/40 text-[10px]">Shop</span>
          {SHOP_LINKS.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setMobile(false)} className="overline text-ink hover:text-gold pl-3" data-testid={`mnav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{n.label}</NavLink>
          ))}
          <div className="h-px bg-black/10 my-2" />
          {user ? (
            <NavLink to="/portal" onClick={() => setMobile(false)} className="overline text-ink hover:text-gold" data-testid="mnav-portal">My Portal</NavLink>
          ) : (
            <>
              <NavLink to="/login" onClick={() => setMobile(false)} className="overline text-ink hover:text-gold" data-testid="mnav-signin">Sign In</NavLink>
              <NavLink to="/signup" onClick={() => setMobile(false)} className="overline text-ink hover:text-gold" data-testid="mnav-signup">Create Account</NavLink>
            </>
          )}
        </div>
      )}
    </header>
  );
}

export function CartSheet() {
  const { items, remove, setQty, total, open, setOpen, clear } = useCart();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const checkout = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer_name: name,
        customer_email: email || null,
      });
      if (!res.data?.url) throw new Error("No checkout URL returned");
      window.location.href = res.data.url;
    } catch (e) {
      console.error(e);
      toast.error("Unable to start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-sheet" className="bg-ivory w-full sm:max-w-md flex flex-col">
        <SheetHeader><SheetTitle className="font-serif text-3xl">Your Cart</SheetTitle>
          <SheetDescription className="sr-only">Review the items in your cart and proceed to checkout.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto py-6 space-y-5">
          {items.length === 0 && <p className="text-muted-foreground">Your cart is empty.</p>}
          {items.map((i) => (
            <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-4 border-b border-black/10 pb-4">
              <img src={i.image} alt={i.name} className="w-16 h-16 object-cover" />
              <div className="flex-1">
                <p className="font-medium text-sm">{i.name}</p>
                <p className="text-gold text-sm">${i.price.toFixed(2)}</p>
                <div className="flex items-center gap-2 mt-2">
                  <input type="number" min="1" value={i.quantity} onChange={(e) => setQty(i.id, parseInt(e.target.value) || 1)} className="w-14 border border-black/20 px-2 py-1 text-sm bg-white" data-testid={`qty-${i.id}`} />
                  <button onClick={() => remove(i.id)} data-testid={`remove-${i.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="border-t border-black/10 pt-4 space-y-3">
            <div className="flex justify-between font-serif text-xl"><span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span></div>
            <input placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-black/20 px-3 py-2 text-sm bg-white" data-testid="checkout-name" />
            <input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-black/20 px-3 py-2 text-sm bg-white" data-testid="checkout-email" />
            <Button onClick={checkout} disabled={loading} data-testid="checkout-button" className="w-full bg-ink text-white rounded-none hover:bg-gold py-6 overline">
              {loading ? "Redirecting..." : "Checkout with Stripe"}
            </Button>
            <button onClick={clear} className="w-full text-xs text-muted-foreground hover:text-ink" data-testid="clear-cart">Clear cart</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function Footer() {
  return (
    <footer data-testid="footer" className="bg-white text-ink border-t border-black/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-4 gap-x-16 gap-y-12">
        <div className="md:col-span-2">
          <img
            src={MEDIA.logo}
            alt="Beverly Hills Paint Co."
            data-testid="footer-logo"
            className="h-24 md:h-28 w-auto"
          />
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-ink/70">Flawless finishes, meticulous preparation, and white-glove craftsmanship for the finest homes in Los Angeles. Over 10 years of experience with a dedicated in-house color designer.</p>
          <div className="flex items-center gap-4 mt-6">
            <a
              href="https://www.instagram.com/beverlyhillspaint"
              target="_blank"
              rel="noreferrer"
              data-testid="social-instagram"
              className="inline-flex items-center gap-2 text-ink hover:text-gold transition-colors text-sm"
              aria-label="Follow us on Instagram @beverlyhillspaint"
            >
              <Instagram size={20} />
              <span>@beverlyhillspaint</span>
            </a>
            <span
              data-testid="social-youtube"
              className="inline-flex items-center gap-2 text-ink/40 text-sm"
            >
              <Youtube size={20} />
              <span>YouTube — coming soon</span>
            </span>
          </div>
        </div>
        <div>
          <p className="eyebrow text-gold mb-5">Contact</p>
          <p className="text-sm text-ink">(310) 845-6163</p>
          <p className="text-sm text-ink">hello@beverlyhillspaintco.com</p>
          <p className="text-sm mt-2 text-ink/70">Beverly Hills, CA 90210</p>
          <p className="text-sm text-ink/70">Mon–Sat · 8am–6pm</p>
        </div>
        <div className="md:pl-8">
          <p className="eyebrow text-gold mb-5">Explore</p>
          <div className="flex flex-col gap-2 text-sm">
            <Link to="/services" className="text-ink hover:text-gold">Services</Link>
            <Link to="/portfolio" className="text-ink hover:text-gold">Portfolio</Link>
            <Link to="/paints" className="text-ink hover:text-gold">Paints we use</Link>
            <Link to="/free-tools" className="text-ink hover:text-gold">Free Tools</Link>
            <Link to="/blog" className="text-ink hover:text-gold">Our Blog</Link>
            <Link to="/courses" className="text-ink hover:text-gold">Color Courses</Link>
            <Link to="/renderings" className="text-ink hover:text-gold">Renderings</Link>
            <Link to="/refer" className="text-ink hover:text-gold" data-testid="footer-refer-link">Refer &amp; Earn</Link>
            <Link to="/portal" className="text-ink hover:text-gold">My Portal</Link>
            <Link to="/contact" className="text-ink hover:text-gold">Get a Quote</Link>
          </div>
        </div>
      </div>
      <div className="border-t border-black/10 py-8 text-center text-xs text-ink/50">© {new Date().getFullYear()} Beverly Hills Paint Co. All rights reserved.</div>
    </footer>
  );
}
